import { playersAbove } from "./band.ts";
import { IGNORED_ISSUES_MAX } from "./issue-ignores.ts";
import { mergeMapGroups, mergeSeams, settleLayout } from "./merge-layout.ts";
import { layerRegion, mergeLayer, type LayerPlace } from "./merge-layers.ts";
import { conflict, mergeKeyed, mergeObject, mergeRecord, pickValue, type MergeRegion, type MergeSide, type MergeTally } from "./merge-rules.ts";
import { EMPTY_TILE, assertTilesetListFits, unpackTile, withTilesetIndex } from "./packed-tile.ts";
import { tilesetUses } from "./tileset-change.ts";
import { framesUnion, sameFrames, tilesetEntry } from "./tileset-frames.ts";
import type { LayerDocument, LayerGroup, MapDocument, TilesetRef, WorldDocument } from "./types.ts";

export type { MergeRegion, MergeSide } from "./merge-rules.ts";

/**
 * The three-way merge of world documents (ADR-086 decision 6): base is the document of the revision a tab
 * holds, mine the tab's document, theirs the newer head. Maps, layers, layer groups, props and entities merge
 * by id, grids cell by cell, map properties, terrain names and entity fields key by key, map groups by id, seams
 * by their pair and a seam's crossings by name (a layout that breaks a rule taken whole from the side the
 * world's region is settled to, `settleLayout`), a map's ignored issues
 * key by key and capped at `IGNORED_ISSUES_MAX` (the first keys in order kept, the cap told as a conflict of the
 * map's own region, since neither side chose what was left out), everything else as one
 * value; a value only one side changed takes that side, and a value both changed differently is a conflict of
 * its region (a layer's chunk, an item, a layer, a map, or the world), settled by the choice made for that
 * region, mine when none is made. Tile cells are compared as the tileset and tile they name, through one
 * tileset list covering all three sides, and written back against the merged list, which keeps a tileset one
 * side removed while the merged world still uses it; a kept entry's frames (ADR-129 decision 3.8) merge by
 * `mergeTilesetEntry`, raising no conflict. The Players band is merged as each layer's side of it.
 * The same inputs always give the same document and the same conflicts, in the order the merge meets them;
 * no input is changed.
 *
 * @example
 * const first = mergeWorlds(base, mine, theirs);
 * const settled = mergeWorlds(base, mine, theirs, Object.fromEntries(first.conflicts.map((r) => [r.id, "theirs"])));
 */

/**
 * A merge's outcome: the document, with each conflicting region settled by its choice or by mine, and every
 * conflicting region, chosen or not.
 */
export interface MergeResult {
  readonly document: WorldDocument;
  readonly conflicts: readonly MergeRegion[];
}

/**
 * The region of a map's own fields, its existence, its layer order and its groups.
 *
 * @param mapId The map.
 * @returns The region.
 */
function mapRegion(mapId: string): MergeRegion {
  return { kind: "map", id: JSON.stringify(["map", mapId]), mapId };
}

/**
 * The region of the world's own fields and terrain names.
 *
 * @returns The region.
 */
function worldRegion(): MergeRegion {
  return { kind: "world", id: JSON.stringify(["world"]) };
}

/**
 * Packs a document's tile cells against another tileset list that holds every tileset the document's own list
 * holds.
 *
 * @param world The document.
 * @param tilesets The other list.
 * @returns The document over that list; the same document when the lists are equal.
 */
function repacked(world: WorldDocument, tilesets: readonly TilesetRef[]): WorldDocument {
  const index = new Map(tilesets.map((tileset, i) => [tileset.id, i]));
  const remap = world.tilesets.map((tileset) => index.get(tileset.id) ?? -1);
  if (remap.length === tilesets.length && remap.every((to, from) => to === from)) {
    return { ...world, tilesets };
  }
  const layer = (l: LayerDocument): LayerDocument => {
    if (l.kind !== "tile") {
      return l;
    }
    const tiles = Uint32Array.from(l.tiles, (packed) => {
      if (packed === EMPTY_TILE) {
        return packed;
      }
      const from = unpackTile(packed).tilesetIndex;
      const to = remap[from];
      if (to === undefined || to < 0) {
        throw new Error(`layer ${l.id} names tileset index ${from}, which the list lacks`);
      }
      return withTilesetIndex(packed, to);
    });
    return { ...l, tiles };
  };
  return { ...world, tilesets, maps: world.maps.map((map) => ({ ...map, layers: map.layers.map(layer) })) };
}

/**
 * Merges the tileset lists as ids: the list of the side that changed it, or theirs followed by mine's
 * additions, less what either side removed, when both did.
 *
 * @param base The base's ids.
 * @param mine Mine's.
 * @param theirs Theirs.
 * @returns The merged ids.
 */
function mergeTilesetIds(base: readonly string[], mine: readonly string[], theirs: readonly string[]): string[] {
  const same = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((id, i) => id === b[i]);
  if (same(mine, theirs) || same(theirs, base)) {
    return [...mine];
  }
  if (same(mine, base)) {
    return [...theirs];
  }
  const removedByMine = (id: string): boolean => base.includes(id) && !mine.includes(id);
  return [...theirs.filter((id) => !removedByMine(id)), ...mine.filter((id) => !theirs.includes(id) && !base.includes(id))];
}

/**
 * Merges one tileset's entry across the three versions, for an entry the merged list keeps (ADR-129 decision
 * 3.8): a side that does not list it adds nothing; when only one side's entry differs from the base's, that
 * side's, the merge's rule for any value; when both differ, or the base has none, the whole entry when either
 * side's is whole and otherwise the union of the two sides' frames, ascending; one neither side lists, kept
 * because the merged world still uses it, takes the base's. No conflict is raised and no cap is checked: a
 * union past a pick cap is a valid document, and the caps bind where picks are made.
 *
 * @param id The tileset.
 * @param b The base's entry, undefined when it lists none.
 * @param m Mine's.
 * @param t Theirs.
 * @returns The entry.
 */
function mergeTilesetEntry(id: string, b: TilesetRef | undefined, m: TilesetRef | undefined, t: TilesetRef | undefined): TilesetRef {
  if (m === undefined || t === undefined) {
    return tilesetEntry(id, (m ?? t ?? b)?.frames);
  }
  if (sameFrames(m.frames, t.frames) || (b !== undefined && sameFrames(t.frames, b.frames))) {
    return tilesetEntry(id, m.frames);
  }
  if (b !== undefined && sameFrames(m.frames, b.frames)) {
    return tilesetEntry(id, t.frames);
  }
  return tilesetEntry(id, m.frames === undefined || t.frames === undefined ? undefined : framesUnion(m.frames, t.frames));
}

/**
 * Merges the Players band (ADR-047): each layer is on the ground side or not as the three versions say, by the
 * same rule as any value, and a band pinned in any version is pinned in the merge at the count of leading
 * layers on the ground side.
 *
 * @param maps The base's, mine's and theirs' map.
 * @param layers The merged layers.
 * @returns The band, or undefined when no version pins one.
 */
function mergeBand(maps: { readonly base: MapDocument; readonly mine: MapDocument; readonly theirs: MapDocument }, layers: readonly LayerDocument[]): number | undefined {
  if (maps.base.playersAbove === undefined && maps.mine.playersAbove === undefined && maps.theirs.playersAbove === undefined) {
    return undefined;
  }
  const ground = (map: MapDocument): Map<string, boolean> => {
    const band = playersAbove(map);
    return new Map(map.layers.map((layer, index) => [layer.id, index < band]));
  };
  const b = ground(maps.base);
  const m = ground(maps.mine);
  const t = ground(maps.theirs);
  const onGround = (id: string): boolean => {
    const mine = m.get(id);
    const theirs = t.get(id);
    if (mine === undefined || theirs === undefined) {
      return mine ?? theirs ?? false;
    }
    return mine === theirs || b.get(id) !== mine ? mine : theirs;
  };
  let count = 0;
  while (count < layers.length && onGround((layers[count] as LayerDocument).id)) {
    count += 1;
  }
  return count;
}

/**
 * Merges one map all three versions hold.
 *
 * @param tally The tally.
 * @param chunkSize The world's chunk size.
 * @param maps The base's, mine's and theirs' map.
 * @returns The merged map.
 */
function mergeMap(tally: MergeTally, chunkSize: number, maps: { readonly base: MapDocument; readonly mine: MapDocument; readonly theirs: MapDocument }): MapDocument {
  const { base, mine, theirs } = maps;
  const region = (): MergeRegion => mapRegion(mine.id);
  const place: LayerPlace = { tally, mapId: mine.id, width: mine.width, height: mine.height, chunkSize };
  const mergers = {
    layers: (b: unknown, m: unknown, t: unknown) =>
      mergeKeyed<LayerDocument>(tally, b as readonly LayerDocument[], m as readonly LayerDocument[], t as readonly LayerDocument[], {
        region: (id) => layerRegion(place, id),
        content: (bl, ml, tl) => mergeLayer(place, bl, ml, tl),
        orderRegion: region,
      }),
    groups: (b: unknown, m: unknown, t: unknown) => {
      if (b === undefined && m === undefined && t === undefined) {
        return undefined;
      }
      const groups = mergeKeyed<LayerGroup>(tally, (b ?? []) as readonly LayerGroup[], (m ?? []) as readonly LayerGroup[], (t ?? []) as readonly LayerGroup[], {
        region,
        content: (bg, mg, tg) => mergeObject(tally, bg, mg, tg, region),
        orderRegion: region,
      });
      return groups.length === 0 && (m === undefined || t === undefined) ? undefined : groups;
    },
    properties: (b: unknown, m: unknown, t: unknown) => mergeRecord(tally, b, m, t, region) ?? {},
    ignoredIssues: (b: unknown, m: unknown, t: unknown) => {
      const merged = mergeRecord(tally, b, m, t, region);
      const keys = Object.keys(merged ?? {}).sort();
      if (keys.length > IGNORED_ISSUES_MAX) {
        conflict(tally, region());
      }
      return keys.length === 0 ? undefined : Object.fromEntries(keys.slice(0, IGNORED_ISSUES_MAX).map((key) => [key, true as const]));
    },
    playersAbove: () => undefined,
  };
  const merged = mergeObject(tally, base, mine, theirs, region, mergers);
  const band = mergeBand(maps, merged.layers);
  return band === undefined ? merged : { ...merged, playersAbove: band };
}

/**
 * Merges three versions of a world.
 *
 * @param base The document of the revision the tab holds.
 * @param mine The tab's document.
 * @param theirs The newer head.
 * @param choices The side each conflicting region takes, by region id; a region not named takes mine.
 * @returns The merged document and every conflicting region.
 * @throws Error when a tile cell names a tileset index beyond its document's list, or when the three sides'
 *   tileset lists together hold more entries than the packed tile form indexes.
 */
export function mergeWorlds(base: WorldDocument, mine: WorldDocument, theirs: WorldDocument, choices: Readonly<Record<string, MergeSide>> = {}): MergeResult {
  const tally: MergeTally = { choices, conflicts: new Map() };
  const superset: TilesetRef[] = [];
  for (const tileset of [...theirs.tilesets, ...mine.tilesets, ...base.tilesets]) {
    if (!superset.some((t) => t.id === tileset.id)) {
      superset.push({ id: tileset.id });
    }
  }
  assertTilesetListFits(superset.length, "the merged tileset list would hold");
  const b = repacked(base, superset);
  const m = repacked(mine, superset);
  const t = repacked(theirs, superset);
  const merged = mergeObject(tally, b, m, t, worldRegion, {
    tilesets: () => superset,
    terrainNames: (bn, mn, tn) => mergeRecord(tally, bn, mn, tn, worldRegion),
    mapGroups: (bg, mg, tg) => mergeMapGroups(tally, bg, mg, tg, worldRegion),
    seams: (bs, ms, ts) => mergeSeams(tally, bs, ms, ts, worldRegion),
    maps: (bm, mm, tm) =>
      mergeKeyed<MapDocument>(tally, bm as readonly MapDocument[], mm as readonly MapDocument[], tm as readonly MapDocument[], {
        region: mapRegion,
        content: (bmap, mmap, tmap) => mergeMap(tally, pickValue(tally, b.chunkSize, m.chunkSize, t.chunkSize, worldRegion), { base: bmap, mine: mmap, theirs: tmap }),
        orderRegion: worldRegion,
      }),
  });
  const listed = mergeTilesetIds(
    base.tilesets.map((ts) => ts.id),
    mine.tilesets.map((ts) => ts.id),
    theirs.tilesets.map((ts) => ts.id),
  );
  const laidOut = settleLayout(merged, { mine: m, theirs: t }, tally, worldRegion);
  const used = new Set<string>();
  for (const use of tilesetUses(laidOut)) {
    used.add(use.tileset);
  }
  const kept = [...listed, ...superset.map((ts) => ts.id).filter((id) => !listed.includes(id) && used.has(id))];
  const [inBase, inMine, inTheirs] = [base, mine, theirs].map((side) => new Map(side.tilesets.map((tileset) => [tileset.id, tileset]))) as [Map<string, TilesetRef>, Map<string, TilesetRef>, Map<string, TilesetRef>];
  const document = repacked(laidOut, kept.map((id) => mergeTilesetEntry(id, inBase.get(id), inMine.get(id), inTheirs.get(id))));
  return { document, conflicts: [...tally.conflicts.values()] };
}
